import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, FormControl } from '@angular/forms';
import {
  SharedModule,
  StringCustomFieldConfig,
  DynamicFormInputComponent,
  FormInputComponent,
  DataService,
  NotificationService
} from '@vendure/admin-ui/core';
import {
  BannerButtonData,
  ButtonPositionType,
  HeroLayoutType,
  HeroSectionData,
  TextAlignment,
  defaultBannerButtonData,
  defaultHeroSectionData,
  NavContext
} from '../ui-types';
import { ID } from '@vendure/core';
import { Asset } from '../generated';
import { forkJoin, Observable } from 'rxjs';
import { DEFAULT_ACTIVE_LANGUAGE } from '../constants';
import { areArraysEqual, clampUnitInterval, normalizeShadowTransparency, safeJSONParse } from '../utils';

@Component({
  selector: 'vdr-hero-section-input',
  template: `
    <vdr-card>
      <!-- Layout Config -->
      <vdr-form-field [label]="'Banner Layout'" class="mb-2">
        <select [formControl]="layoutTypeControl">
          <option value="left-text-full-media">Left Text</option>
          <option value="right-text-full-media">Right Text</option>
          <option value="middle-text-full-media">Middle Text</option>
          <option value="custom-text-location-full-media">Custom Text Coordinates</option>
        </select>
      </vdr-form-field>

      <div class="clr-row mb-2" *ngIf="layoutTypeControl.value === 'custom-text-location-full-media'">
        <div class="clr-col-3"><vdr-form-field [label]="'Text X-Position (0-1)'"><input type="number" [formControl]="textPositionXControl" step="0.01"/></vdr-form-field></div>
        <div class="clr-col-3"><vdr-form-field [label]="'Text Y-Position (0-1)'"><input type="number" [formControl]="textPositionYControl" step="0.01"/></vdr-form-field></div>
      </div>
      <div class="clr-row" *ngIf="layoutTypeControl.value === 'custom-text-location-full-media'">
        <div class="clr-col-3"><vdr-form-field [label]="'Mobile Text X-Position (0-1)'"><input type="number" [formControl]="mobileTextPositionXControl" step="0.01"/></vdr-form-field></div>
        <div class="clr-col-3"><vdr-form-field [label]="'Mobile Text Y-Position (0-1)'"><input type="number" [formControl]="mobileTextPositionYControl" step="0.01"/></vdr-form-field></div>
      </div>

      <hr />

      <!-- Medias Config -->
      <div class="clr-row">
        <div class="clr-col-12">
          <p><strong>Medias</strong></p>
          <vdr-form-field [label]="'Assets'" class="mt-1 mb-2">
            <vdr-dynamic-form-input #mediasInput
              [control]="mediasControl"
              [def]="{ list: true, type: 'relation', entity: 'Asset', ui: { component: 'relation-form-input' } }">
            </vdr-dynamic-form-input>
          </vdr-form-field>

          <vdr-form-field [label]="'Mobile Assets'" class="mb-2">
            <vdr-dynamic-form-input #mobileMediasInput
              [control]="mobileMediasControl"
              [def]="{ list: true, type: 'relation', entity: 'Asset', ui: { component: 'relation-form-input' } }">
            </vdr-dynamic-form-input>
          </vdr-form-field>

          <vdr-form-field [label]="'Overlay Opacity (0.0 - 1.0)'" class="mb-2">
            <input type="number" [formControl]="overlayOpacityControl" min="0" max="1" step="0.1" />
          </vdr-form-field>
        </div>
      </div>

      <hr />

      <!-- Content Config -->
      <p><strong>Content & Typography</strong></p>
      <vdr-form-field [label]="'Title'" class="mt-1 mb-2">
        <vdr-dynamic-form-input [control]="titleControl" [def]="titleTextValueDef"></vdr-dynamic-form-input>
      </vdr-form-field>

      <div class="clr-row mb-2">
        <vdr-form-field [label]="'Title Font Size (px)'" class="clr-col-6">
          <input type="number" [formControl]="titleFontSizeControl" />
        </vdr-form-field>
        <vdr-form-field [label]="'Mobile Title Font Size (px)'" class="clr-col-6">
          <input type="number" [formControl]="mobileTitleFontSizeControl" />
        </vdr-form-field>
      </div>

      <vdr-form-field [label]="'Subtitle'" class="mb-2">
        <vdr-dynamic-form-input [control]="subtitleControl" [def]="subtitleTextValueDef"></vdr-dynamic-form-input>
      </vdr-form-field>

      <div class="clr-row mb-2">
        <vdr-form-field [label]="'Subtitle Font Size (px)'" class="clr-col-6">
          <input type="number" [formControl]="subtitleFontSizeControl" />
        </vdr-form-field>
        <vdr-form-field [label]="'Mobile Subtitle Font Size (px)'" class="clr-col-6">
          <input type="number" [formControl]="mobileSubtitleFontSizeControl" />
        </vdr-form-field>
      </div>

      <vdr-form-field [label]="'Text Color'" class="mb-2">
        <input type="color" [formControl]="textColorControl" />
      </vdr-form-field>

      <vdr-form-field
          *ngIf="layoutTypeControl.value === 'custom-text-location-full-media'"
          [label]="'Text Block Width (0-1)'" class="mb-2">
          <input type="number" [formControl]="textWidthControl" step="0.01" />
      </vdr-form-field>

      <hr />

      <!-- Button Config -->
      <p><strong>Button</strong></p>
      <div>
        <vdr-form-field class="mt-1 mb-2">
            <vdr-dynamic-form-input
              [control]="buttonControl"
              [def]="buttonInputDef"
            >
            </vdr-dynamic-form-input>
        </vdr-form-field>
        <div class="clr-row mb-2" *ngIf="heroSectionData?.button?.enabled">
          <vdr-form-field [label]="'Button Position'" class="clr-col-4">
            <select [formControl]="buttonPositionControl">
              <option value="below-text">Below Text</option>
              <option value="custom">Custom</option>
            </select>
          </vdr-form-field>
          <div class="clr-col-4"
              *ngIf="buttonPositionControl.value === 'below-text'">
              <vdr-form-field [label]="'Button Alignment'">
                <select [formControl]="buttonAlignmentControl">
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </vdr-form-field>
          </div>
          <div class="clr-col-4" *ngIf="buttonPositionControl.value === 'custom'">
            <vdr-form-field [label]="'Button X-Position (0-1)'">
              <input type="number" [formControl]="buttonLocationXControl" min="0" max="1" step="0.01">
            </vdr-form-field>
          </div>
          <div class="clr-col-4" *ngIf="buttonPositionControl.value === 'custom'">
            <vdr-form-field [label]="'Button Y-Position (0-1)'">
              <input type="number" [formControl]="buttonLocationYControl" min="0" max="1" step="0.01">
            </vdr-form-field>
          </div>
        </div>

        <div class="clr-row mb-2" *ngIf="heroSectionData?.button?.enabled">
          <vdr-form-field [label]="'Mobile Button Position'" class="clr-col-4">
            <select [formControl]="buttonPositionMobileControl">
              <option value="below-text">Below Text</option>
              <option value="custom">Custom</option>
            </select>
          </vdr-form-field>
          <div class="clr-col-4"
              *ngIf="buttonPositionMobileControl.value === 'below-text'">
              <vdr-form-field [label]="'Mobile Button Alignment'">
                <select [formControl]="buttonAlignmentMobileControl">
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </vdr-form-field>
          </div>
          <div class="clr-col-4" *ngIf="buttonPositionMobileControl.value === 'custom'">
            <vdr-form-field [label]="'Mobile Button X-Position (0-1)'">
              <input type="number" [formControl]="buttonLocationXMobileControl" min="0" max="1" step="0.01">
            </vdr-form-field>
          </div>
          <div class="clr-col-4" *ngIf="buttonPositionMobileControl.value === 'custom'">
            <vdr-form-field [label]="'Mobile Button Y-Position (0-1)'">
              <input type="number" [formControl]="buttonLocationYMobileControl" min="0" max="1" step="0.01">
            </vdr-form-field>
          </div>
        </div>
      </div>
    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule],
})
export class HeroSectionInputComponent
  implements OnInit, AfterViewInit, FormInputComponent<StringCustomFieldConfig>
{
  @ViewChild('mediasInput') mediasInput: DynamicFormInputComponent;
  @ViewChild('mobileMediasInput') mobileMediasInput: DynamicFormInputComponent;

  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  // Form controls
  layoutTypeControl = new FormControl();
  mediasControl = new UntypedFormControl();
  mobileMediasControl = new UntypedFormControl();
  titleControl = new FormControl();
  subtitleControl = new FormControl();
  titleFontSizeControl = new FormControl();
  subtitleFontSizeControl = new FormControl();
  mobileTitleFontSizeControl = new FormControl();
  mobileSubtitleFontSizeControl = new FormControl();
  textColorControl = new FormControl();
  textPositionXControl = new FormControl();
  textPositionYControl = new FormControl();
  mobileTextPositionXControl = new FormControl();
  mobileTextPositionYControl = new FormControl();
  textWidthControl = new FormControl();
  overlayOpacityControl = new FormControl();
  buttonControl = new UntypedFormControl();
  buttonPositionControl = new FormControl();
  buttonAlignmentControl = new FormControl();
  buttonLocationXControl = new FormControl();
  buttonLocationYControl = new FormControl();
  buttonPositionMobileControl = new FormControl();
  buttonAlignmentMobileControl = new FormControl();
  buttonLocationXMobileControl = new FormControl();
  buttonLocationYMobileControl = new FormControl();

  heroSectionData: HeroSectionData;

  activeLanguageCode = DEFAULT_ACTIVE_LANGUAGE;
  languageChanges$?: Observable<unknown>;
  navContext?: NavContext;
  titleTextValueDef: any;
  subtitleTextValueDef: any;
  buttonInputDef: any;


  constructor(
    private dataService: DataService,
    private notificationService: NotificationService) {}

  ngOnInit() {
    const args = (this.config as any)?.ui?.args ?? {};
    if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) this.activeLanguageCode = args.activeLanguageCode;
    if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
    this.navContext = (this.config?.ui?.options as any)?.navContext as NavContext | undefined;
    this.buttonInputDef = {
      type: 'string',
      ui: {
        component: 'banner-button-form',
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
        options: {
          navContext: this.navContext,
        },
      },
    };
    this.titleTextValueDef = this.createTextValueDef();
    this.subtitleTextValueDef = this.createTextValueDef();
    this.heroSectionData = safeJSONParse<HeroSectionData>(
      this.formControl.value,
      this.notificationService,
      defaultHeroSectionData,
    )!;
    this.initializeFormControls();

    if (this.heroSectionData && this.heroSectionData?.mediaIds?.length > 0) {
      this.loadAssetsByIds(this.heroSectionData.mediaIds).subscribe((assets) => {
        this.mediasControl.setValue(assets);
        if (this.mediasInput) {
          this.mediasInput.writeValue(assets);
        }
      });
    } else {
      this.mediasControl.setValue([]);
    }

    if (this.heroSectionData && this.heroSectionData?.mobileMediaIds?.length > 0) {
      this.loadAssetsByIds(this.heroSectionData.mobileMediaIds).subscribe((assets) => {
        this.mobileMediasControl.setValue(assets);
        if (this.mobileMediasInput) {
          this.mobileMediasInput.writeValue(assets);
        }
      });
    } else {
      this.mobileMediasControl.setValue([]);
    }
  }

  private createTextValueDef(): any {
    return {
      type: 'string',
      ui: {
        component: 'text-value-input',
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
      },
    };
  }

  ngAfterViewInit() {
    if (this.mediasInput) {
      this.mediasInput.registerOnChange(this.onMediasChange);
    }
    if (this.mobileMediasInput) {
      this.mobileMediasInput.registerOnChange(this.onMobileMediasChange);
    }
    this.layoutTypeControl.valueChanges.subscribe(this.onLayoutTypeChange);
    this.titleControl.valueChanges.subscribe(this.onTitleChange);
    this.subtitleControl.valueChanges.subscribe(this.onSubtitleChange);
    this.titleFontSizeControl.valueChanges.subscribe(this.onTitleFontSizeChange);
    this.subtitleFontSizeControl.valueChanges.subscribe(this.onSubtitleFontSizeChange);
    this.mobileTitleFontSizeControl.valueChanges.subscribe(this.onMobileTitleFontSizeChange);
    this.mobileSubtitleFontSizeControl.valueChanges.subscribe(this.onMobileSubtitleFontSizeChange);
    this.textColorControl.valueChanges.subscribe(this.onTextColorChange);
    this.textPositionXControl.valueChanges.subscribe(this.onTextPositionXChange);
    this.textPositionYControl.valueChanges.subscribe(this.onTextPositionYChange);
    this.mobileTextPositionXControl.valueChanges.subscribe(this.onMobileTextPositionXChange);
    this.mobileTextPositionYControl.valueChanges.subscribe(this.onMobileTextPositionYChange);
    this.textWidthControl.valueChanges.subscribe(this.onTextWidthChange);
    this.overlayOpacityControl.valueChanges.subscribe(this.onOverlayOpacityChange);
    this.buttonControl.valueChanges.subscribe(this.onButtonChange);
    this.buttonPositionControl.valueChanges.subscribe(this.onButtonPositionChange);
    this.buttonAlignmentControl.valueChanges.subscribe(this.onButtonAlignmentChange);
    this.buttonLocationXControl.valueChanges.subscribe(this.onButtonLocationXChange);
    this.buttonLocationYControl.valueChanges.subscribe(this.onButtonLocationYChange);
    this.buttonPositionMobileControl.valueChanges.subscribe(this.onButtonPositionMobileChange);
    this.buttonAlignmentMobileControl.valueChanges.subscribe(this.onButtonAlignmentMobileChange);
    this.buttonLocationXMobileControl.valueChanges.subscribe(this.onButtonLocationXMobileChange);
    this.buttonLocationYMobileControl.valueChanges.subscribe(this.onButtonLocationYMobileChange);
  }

  private initializeFormControls() {
    this.layoutTypeControl.setValue(this.heroSectionData?.layoutType);
    // mediasControl and mobileMediasControl are set after loading assets
    this.titleControl.setValue(this.toTextValueJson(this.heroSectionData?.title));
    this.subtitleControl.setValue(this.toTextValueJson(this.heroSectionData?.subtitle));
    this.titleFontSizeControl.setValue(this.heroSectionData?.titleFontSize);
    this.subtitleFontSizeControl.setValue(this.heroSectionData?.subtitleFontSize);
    this.mobileTitleFontSizeControl.setValue(this.heroSectionData?.mobileTitleFontSize);
    this.mobileSubtitleFontSizeControl.setValue(this.heroSectionData?.mobileSubtitleFontSize);
    this.textColorControl.setValue(this.heroSectionData?.textColor);
    this.textPositionXControl.setValue(this.heroSectionData?.textPositionX ?? 0.10);
    this.textPositionYControl.setValue(this.heroSectionData?.textPositionY ?? 0.20);
    this.mobileTextPositionXControl.setValue(this.heroSectionData?.mobileTextPositionX ?? 0.10);
    this.mobileTextPositionYControl.setValue(this.heroSectionData?.mobileTextPositionY ?? 0.20);
    this.textWidthControl.setValue(this.heroSectionData?.textWidth ?? 0.60);
    const shadowTransparency = normalizeShadowTransparency(this.heroSectionData?.shadowTransparency);
    if (this.heroSectionData) {
      this.heroSectionData.shadowTransparency = shadowTransparency;
    }
    this.overlayOpacityControl.setValue(1.0 - shadowTransparency);
    this.buttonControl.setValue(JSON.stringify(this.heroSectionData?.button));
    this.buttonPositionControl.setValue(this.heroSectionData?.buttonPosition);
    this.buttonAlignmentControl.setValue(this.heroSectionData?.buttonAlignment ?? TextAlignment.Center);
    this.buttonLocationXControl.setValue(this.heroSectionData?.buttonLocationX);
    this.buttonLocationYControl.setValue(this.heroSectionData?.buttonLocationY);
    this.buttonPositionMobileControl.setValue(this.heroSectionData?.buttonPositionMobile);
    this.buttonAlignmentMobileControl.setValue(this.heroSectionData?.buttonAlignmentMobile ?? TextAlignment.Center);
    this.buttonLocationXMobileControl.setValue(this.heroSectionData?.buttonLocationXMobile);
    this.buttonLocationYMobileControl.setValue(this.heroSectionData?.buttonLocationYMobile);
  }

  private loadAssetsByIds(ids: ID[]): Observable<Asset[]> {
    return forkJoin(
      ids?.map((id) =>
        this.dataService.product.getAsset(id.toString())?.mapSingle((data) => data.asset)
      )
    ) as Observable<Asset[]>;
  }

  private onMediasChange = (medias: Asset[]) => {
    const changedAssetIds = medias?.map((asset) => asset.id);
    if (
      this.heroSectionData &&
      !areArraysEqual(this.heroSectionData.mediaIds, changedAssetIds)
    ) {
      this.heroSectionData.mediaIds = changedAssetIds;
      this.updateJsonString();
    }
  };

  private onMobileMediasChange = (medias: Asset[]) => {
    const changedAssetIds = medias?.map((asset) => asset.id);
    if (
      this.heroSectionData &&
      !areArraysEqual(this.heroSectionData.mobileMediaIds, changedAssetIds)
    ) {
      this.heroSectionData.mobileMediaIds = changedAssetIds;
      this.updateJsonString();
    }
  };

  private onLayoutTypeChange = (value: HeroLayoutType) => {
    if (this.heroSectionData?.layoutType !== value) {
      this.heroSectionData!.layoutType = value;
      this.updateJsonString();
    }
  };

  private onTitleChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.heroSectionData?.title) !== JSON.stringify(next)) {
      this.heroSectionData!.title = next;
      this.updateJsonString();
    }
  };

  private onSubtitleChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.heroSectionData?.subtitle) !== JSON.stringify(next)) {
      this.heroSectionData!.subtitle = next;
      this.updateJsonString();
    }
  };

  private onTitleFontSizeChange = (value: number) => {
    if (this.heroSectionData?.titleFontSize !== value) {
      this.heroSectionData!.titleFontSize = value;
      this.updateJsonString();
    }
  };

  private onSubtitleFontSizeChange = (value: number) => {
    if (this.heroSectionData?.subtitleFontSize !== value) {
      this.heroSectionData!.subtitleFontSize = value;
      this.updateJsonString();
    }
  };

  private onMobileTitleFontSizeChange = (value: number) => {
    if (this.heroSectionData?.mobileTitleFontSize !== value) {
      this.heroSectionData!.mobileTitleFontSize = value;
      this.updateJsonString();
    }
  };


  private onMobileSubtitleFontSizeChange = (value: number) => {
    if (this.heroSectionData?.mobileSubtitleFontSize !== value) {
      this.heroSectionData!.mobileSubtitleFontSize = value;
      this.updateJsonString();
    }
  };

  private onTextColorChange = (value: string) => {
    if (this.heroSectionData?.textColor !== value) {
      this.heroSectionData!.textColor = value;
      this.updateJsonString();
    }
  };

  private onTextPositionXChange = (value: number) => {
    if (this.heroSectionData?.textPositionX !== value) {
      this.heroSectionData!.textPositionX = value;
      this.updateJsonString();
    }
  };

  private onTextPositionYChange = (value: number) => {
    if (this.heroSectionData?.textPositionY !== value) {
      this.heroSectionData!.textPositionY = value;
      this.updateJsonString();
    }
  };

  private onMobileTextPositionXChange = (value: number) => {
    if (this.heroSectionData?.mobileTextPositionX !== value) {
      this.heroSectionData!.mobileTextPositionX = value;
      this.updateJsonString();
    }
  };

  private onMobileTextPositionYChange = (value: number) => {
    if (this.heroSectionData?.mobileTextPositionY !== value) {
      this.heroSectionData!.mobileTextPositionY = value;
      this.updateJsonString();
    }
  };

  private onTextWidthChange = (value: number) => {
    if (this.heroSectionData?.textWidth !== value) {
      this.heroSectionData!.textWidth = value;
      this.updateJsonString();
    }
  };

  private onOverlayOpacityChange = (value: number) => {
    const shadowTransparency = 1.0 - clampUnitInterval(value);
    if (this.heroSectionData?.shadowTransparency !== shadowTransparency) {
      this.heroSectionData!.shadowTransparency = shadowTransparency;
      this.updateJsonString();
    }
  };

  private onButtonChange = (value: string) => {
    if (JSON.stringify(this.heroSectionData?.button) !== value) {
      this.heroSectionData!.button = safeJSONParse<BannerButtonData>(
        value,
        this.notificationService,
        defaultBannerButtonData
      )!;
      this.updateJsonString();
    }
  };

  private onButtonPositionChange = (value: ButtonPositionType) => {
    if (this.heroSectionData?.buttonPosition !== value) {
      this.heroSectionData!.buttonPosition = value;
      this.updateJsonString();
    }
  };

  private onButtonAlignmentChange = (value: TextAlignment) => {
    if (this.heroSectionData?.buttonAlignment !== value) {
      this.heroSectionData!.buttonAlignment = value;
      this.updateJsonString();
    }
  };

  private onButtonLocationXChange = (value: number) => {
    if (this.heroSectionData?.buttonLocationX !== value) {
      this.heroSectionData!.buttonLocationX = value;
      this.updateJsonString();
    }
  };

  private onButtonLocationYChange = (value: number) => {
    if (this.heroSectionData?.buttonLocationY !== value) {
      this.heroSectionData!.buttonLocationY = value;
      this.updateJsonString();
    }
  };

  private onButtonPositionMobileChange = (value: ButtonPositionType) => {
    if (this.heroSectionData?.buttonPositionMobile !== value) {
      this.heroSectionData!.buttonPositionMobile = value;
      this.updateJsonString();
    }
  };

  private onButtonAlignmentMobileChange = (value: TextAlignment) => {
    if (this.heroSectionData?.buttonAlignmentMobile !== value) {
      this.heroSectionData!.buttonAlignmentMobile = value;
      this.updateJsonString();
    }
  };

  private onButtonLocationXMobileChange = (value: number) => {
    if (this.heroSectionData?.buttonLocationXMobile !== value) {
      this.heroSectionData!.buttonLocationXMobile = value;
      this.updateJsonString();
    }
  };

  private onButtonLocationYMobileChange = (value: number) => {
    if (this.heroSectionData?.buttonLocationYMobile !== value) {
      this.heroSectionData!.buttonLocationYMobile = value;
      this.updateJsonString();
    }
  };

  private toTextValueJson(raw: any): string {
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && parsed.i18n) {
          return JSON.stringify(parsed);
        }
      } catch {
        // treat as plain text fallback
      }
    }
    if (raw && typeof raw === 'object' && raw.i18n) return JSON.stringify(raw);
    return JSON.stringify({ format: 'plain', i18n: { [this.activeLanguageCode]: raw ?? '' } });
  }

  private parseTextValue(raw: string | null | undefined): any {
    if (!raw) return { format: 'plain', i18n: {} };
    try { return JSON.parse(raw); } catch { return { format: 'plain', i18n: {} }; }
  }

  private updateJsonString() {
    const jsonString = JSON.stringify(this.heroSectionData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
